/* Sales — a register read back: what sold, at what price, by month and by
 * count. One board for two registers: the yarn one (the default) and, with
 * `product="waste"`, the waste one, where the "count" is a waste type, the
 * buyer is named, and the last money column is what the customer pays
 * with TCS added rather than after TDS.
 *
 * A port of `Sales Dashboard.dc.html`. The design works on one row per
 * invoice line, so the page does too: every figure is a sum over the lines
 * in the selection, and "invoices" counts lines, as the design's KPI does.
 * The design's two props are fixed here — values are ex-mill, and the
 * invoice table shows fifteen rows.
 */

import { useMemo, useRef, useState, type CSSProperties } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import { sales } from '../lib/api_sales'
import type { Product, SaleLine } from '../lib/api_sales'
import { useAsync } from '../lib/useAsync'
import { useCan } from '../lib/auth'
import { Blueprint, Empty, ErrorBox, KpiRow, LoadElsewhere, Loading, PanelHead } from '../components/ui'
import { num } from '../lib/format'

const INVOICE_ROWS = 15
const R: CSSProperties = { textAlign: 'right' }

// One colour and dash per count, by value rank — the same ten the design uses.
const PAL = [
  'var(--color-accent-700)', 'var(--color-accent-400)', 'var(--color-neutral-800)',
  'var(--color-accent-900)', 'var(--color-neutral-500)', 'var(--color-accent-500)',
  'var(--color-neutral-600)', 'var(--color-accent-800)', 'var(--color-accent-600)',
  'var(--color-neutral-700)',
]
const DASH = ['0', '0', '0', '0', '6 4', '6 4', '2 3', '6 4', '2 3', '2 3']

/** The design's row shape: a line with its money columns filled in. `pay`
 *  is the register's last column either way — yarn after TDS, waste with
 *  TCS added. */
interface Inv {
  no: string; d: string; count: string; customer: string; bags: number; kg: number; price: number
  exmill: number; gst: number; total: number; pay: number
}

const toInv = (l: SaleLine): Inv => ({
  no: l.invoice_no, d: l.invoice_date, count: l.count_label, customer: l.customer ?? '',
  bags: l.bags ?? 0, kg: l.quantity_kg, price: l.price_per_kg,
  exmill: l.ex_mill_value ?? l.quantity_kg * l.price_per_kg,
  gst: l.gst ?? 0, total: l.invoice_value ?? 0, pay: l.payable ?? 0,
})

/** The words that differ between the registers: yarn is sold by count,
 *  waste by type. The yarn column is what the page said before waste
 *  joined it, word for word. */
const WORDS = {
  yarn: {
    thing: 'count', all: 'All counts', Thing: 'Count', Things: 'Counts', top: 'Top count',
    table: 'Count by count', last: 'Payable', loading: 'the sales register',
    register: 'yarn sales register', cli: 'load-sales FILE.xlsx',
    upload: 'Load a newer sales register',
  },
  waste: {
    thing: 'type', all: 'All types', Thing: 'Waste type', Things: 'Types', top: 'Top waste type',
    table: 'Waste by type', last: 'Total', loading: 'the waste sales register',
    register: 'waste sales register', cli: 'load-sales --waste FILE.xlsx',
    upload: 'Load a newer waste sales register',
  },
}

// ---------------------------------------------------------------- format
const cr = (v: number) => '₹' + num(v / 1e7, 2) + ' Cr'
const lakh = (v: number) => '₹' + num(v / 1e5, 1) + ' L'
const money = (v: number) => (Math.abs(v) >= 1e7 ? cr(v) : lakh(v))
const t = (v: number) => num(v / 1000, 1) + ' t'
const monthName = (k: string, short = false) =>
  new Date(k + '-01T00:00:00').toLocaleDateString('en-IN',
    short ? { month: 'short' } : { month: 'long', year: 'numeric' })
const dayName = (d: string) =>
  new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
const daysIn = (k: string) => new Date(+k.slice(0, 4), +k.slice(5, 7), 0).getDate()
/** The next "round" number at or above `v`: 1, 2, 2.5, 5, 10 × a power of ten. */
function nice(v: number) {
  const p = Math.pow(10, Math.floor(Math.log10(v)))
  const m = v / p
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p
}
const sum = <T,>(a: T[], f: (x: T) => number) => a.reduce((s, x) => s + f(x), 0)

export default function Sales({ product = 'yarn' }: { product?: Product }) {
  const W = WORDS[product]
  const { data, error, loading, reload } = useAsync(() => sales.lines(product), [product])
  const [period, setPeriod] = useState('all')
  const [count, setCount] = useState(W.all)
  const [bHov, setBHov] = useState<string | null>(null)
  const [pHov, setPHov] = useState<string | null>(null)
  const [pOff, setPOff] = useState<Record<string, boolean>>({})

  const all = useMemo(() => (data?.lines ?? []).map(toInv), [data])

  if (error) return <main style={MAIN_STYLE}><ErrorBox error={error} onRetry={reload} /></main>
  if (loading && !data) return <main style={MAIN_STYLE}><Loading what={W.loading} /></main>
  if (!all.length) {
    return (
      <main style={MAIN_STYLE}>
        <Empty title="No sales on file yet">
          Load the {W.register} with <code>app.cli {W.cli}</code> or the button
          below and this fills in.
        </Empty>
        <UploadLine product={product} onLoaded={reload} />
      </main>
    )
  }

  // ---- the selection --------------------------------------------------
  const months = [...new Set(all.map((i) => i.d.slice(0, 7)))].sort()
  const per = period === 'all' || months.includes(period) ? period : 'all'
  const valueOfAll: Record<string, number> = {}
  all.forEach((i) => { valueOfAll[i.count] = (valueOfAll[i.count] || 0) + i.exmill })
  const countsAll = Object.keys(valueOfAll).sort((a, b) => valueOfAll[b] - valueOfAll[a])
  const color: Record<string, string> = {}, dash: Record<string, string> = {}
  countsAll.forEach((c, i) => { color[c] = PAL[i % PAL.length]; dash[c] = DASH[i % DASH.length] })
  const cnt = countsAll.includes(count) ? count : W.all
  const byCount = (i: Inv) => cnt === W.all || i.count === cnt
  const inv = all.filter((i) => byCount(i) && (per === 'all' || i.d.slice(0, 7) === per))
  const kgT = sum(inv, (i) => i.kg), val = sum(inv, (i) => i.exmill), gst = sum(inv, (i) => i.gst)
  const avg = kgT ? val / kgT : 0

  // The month before, for the KPI deltas; nothing when the whole year shows.
  let prev: Inv[] | null = null, prevLabel = ''
  if (per !== 'all') {
    const ix = months.indexOf(per)
    if (ix > 0) {
      prev = all.filter((i) => byCount(i) && i.d.slice(0, 7) === months[ix - 1])
      prevLabel = monthName(months[ix - 1], true)
    }
  }
  const vs = (a: number, b: number) => {
    if (!b) return ''
    const p = (a / b - 1) * 100
    return (p >= 0 ? '▲ ' : '▼ ') + num(Math.abs(p), 1) + '% vs ' + prevLabel
  }
  const pKg = prev ? sum(prev, (i) => i.kg) : 0, pVal = prev ? sum(prev, (i) => i.exmill) : 0
  const pAvg = pKg ? pVal / pKg : 0
  const spanDays = (() => {
    if (!inv.length) return 1
    if (per !== 'all') return daysIn(per)
    const ds = inv.map((i) => i.d).sort()
    return Math.round((+new Date(ds[ds.length - 1]) - +new Date(months[0] + '-01')) / 864e5) + 1
  })()
  const cv: Record<string, number> = {}
  inv.forEach((i) => { cv[i.count] = (cv[i.count] || 0) + i.exmill })
  const topC = Object.keys(cv).sort((a, b) => cv[b] - cv[a])[0]

  const kpis = [
    { label: 'Sales (ex-mill)', value: num(val / 1e7, 2), unit: '₹ Cr',
      note: prev ? vs(val, pVal) : num(val / spanDays / 1e5, 1) + ' L a day on average' },
    { label: 'Quantity sold', value: num(kgT / 1000, 1), unit: 't',
      note: prev ? vs(kgT, pKg)
        : num(sum(inv, (i) => i.bags)) + ' bags · ' + num(kgT / spanDays / 1000, 1) + ' t a day' },
    { label: 'Average realisation', value: '₹' + num(avg, 1), unit: '/kg',
      note: prev ? (avg >= pAvg ? '▲ ₹' : '▼ ₹') + num(Math.abs(avg - pAvg), 1) + '/kg vs ' + prevLabel
        : 'ex-mill, weighted by kg' },
    { label: 'Invoices', value: num(inv.length),
      note: inv.length ? num(kgT / inv.length) + ' kg per invoice on average' : '' },
    { label: 'GST billed', value: num(gst / 1e5, 1), unit: '₹ L',
      note: val ? num(gst / val * 100, 1) + '% of ex-mill value' : '' },
    { label: W.top, value: cnt === W.all ? (topC || '–') : cnt,
      note: topC && val ? num(cv[topC] / val * 100, 0) + '% of ex-mill value' : '' },
  ]

  // ---- bars: months over the year, days within a month, stacked by count
  const buckets = per === 'all' ? months
    : Array.from({ length: daysIn(per) }, (_, k) => per + '-' + String(k + 1).padStart(2, '0'))
  const bKey = (i: Inv) => (per === 'all' ? i.d.slice(0, 7) : i.d)
  const bVal = (b: string) => sum(inv.filter((i) => bKey(i) === b), (i) => i.exmill)
  const bMax = nice(Math.max(1, ...buckets.map(bVal)) * 1.1)
  const tickMoney = (v: number) =>
    v >= 1e7 ? num(v / 1e7, v % 1e7 ? 1 : 0) + ' Cr' : num(v / 1e5, 0) + ' L'
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ label: tickMoney(bMax * f), pos: f * 100 + '%' }))
  const segCounts = cnt === W.all ? countsAll : [cnt]
  const hb = bHov && buckets.includes(bHov) ? bHov : null
  const bars = buckets.map((b, ix) => {
    const v = bVal(b)
    return {
      key: b, v,
      label: per === 'all' ? monthName(b, true)
        : (ix % 2 === 0 || buckets.length < 16 ? String(+b.slice(8)) : ''),
      op: hb && hb !== b ? 0.45 : 1,
      segs: segCounts
        .map((c) => ({ h: sum(inv.filter((i) => bKey(i) === b && i.count === c), (i) => i.exmill) / bMax * 100, bg: color[c] }))
        .filter((s) => s.h > 0),
      showVal: per === 'all' && v > 0, top: v / bMax * 100 + '%',
    }
  })
  const bPos = (b: string) => (buckets.indexOf(b) + 0.5) / buckets.length * 100
  const barHov = hb ? (() => {
    const its = inv.filter((i) => bKey(i) === hb)
    const k = sum(its, (i) => i.kg), e = sum(its, (i) => i.exmill)
    return {
      left: bPos(hb) + '%',
      shift: bPos(hb) > 60 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)',
      title: per === 'all' ? monthName(hb)
        : new Date(hb + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }),
      value: its.length ? money(e) : 'no invoices',
      sub: its.length ? t(k) + ' · ₹' + num(e / k, 1) + '/kg · ' + its.length + ' invoices' : '',
      rows: segCounts.map((c) => {
        const ci = its.filter((i) => i.count === c)
        const s = sum(ci, (i) => i.exmill)
        return { label: c, bg: color[c], kg: ci.length ? t(sum(ci, (i) => i.kg)) : '', v: ci.length ? money(s) : '', s }
      }).filter((r) => r.s > 0).sort((a, b) => b.s - a.s),
    }
  })() : null
  const barGap = per === 'all' ? 28 : 3, barMaxW = per === 'all' ? 96 : 28

  // ---- realisation lines, always across every month on file -------------
  const pickable = cnt === W.all
    ? countsAll.filter((c) => all.filter((i) => i.count === c).length >= 3) : [cnt]
  const lineCounts = pickable.filter((c) => !pOff[c])
  const mAvg = (c: string, m: string) => {
    const a = all.filter((i) => i.count === c && i.d.slice(0, 7) === m)
    const k = sum(a, (i) => i.kg)
    return k ? sum(a, (i) => i.exmill) / k : null
  }
  const pv = (lineCounts.length ? lineCounts : pickable)
    .flatMap((c) => months.map((m) => mAvg(c, m))).filter((v): v is number => v !== null)
  const pLo = pv.length ? Math.min(...pv) : 0, pHi = pv.length ? Math.max(...pv) : 1
  const p0 = Math.floor((pLo - 8) / 10) * 10, p1 = Math.ceil((pHi + 8) / 10) * 10
  const pY = (v: number) => 240 - (v - p0) / ((p1 - p0) || 1) * 240
  const pX = (ix: number) => (ix + 0.5) / months.length * 1000
  const pLines = lineCounts.map((c) => ({
    c, bg: color[c], dash: dash[c],
    points: months.map((m, ix) => { const v = mAvg(c, m); return v === null ? null : pX(ix).toFixed(1) + ',' + pY(v).toFixed(1) })
      .filter(Boolean).join(' '),
  })).filter((l) => l.points)
  const pTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ label: '₹' + num(p0 + (p1 - p0) * f), pos: f * 100 + '%' }))
  const hp = pHov && months.includes(pHov) ? pHov : null
  const pDots = hp ? lineCounts.map((c) => {
    const v = mAvg(c, hp)
    return v === null ? null : { c, bg: color[c], left: pX(months.indexOf(hp)) / 10 + '%', bottom: (v - p0) / ((p1 - p0) || 1) * 100 + '%' }
  }).filter((x): x is NonNullable<typeof x> => x !== null) : []
  const priceHov = hp ? {
    x: pX(months.indexOf(hp)), left: pX(months.indexOf(hp)) / 10 + '%',
    shift: pX(months.indexOf(hp)) > 600 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)',
    title: monthName(hp) + ' · ₹/kg',
    rows: lineCounts.map((c) => ({ label: c, bg: color[c], s: mAvg(c, hp) }))
      .filter((r): r is { label: string; bg: string; s: number } => r.s !== null)
      .sort((a, b) => b.s - a.s),
  } : null

  // ---- count table ------------------------------------------------------
  const rows = (cnt === W.all ? countsAll : [cnt]).map((c) => {
    const a = inv.filter((i) => i.count === c)
    if (!a.length) return null
    const k = sum(a, (i) => i.kg), v = sum(a, (i) => i.exmill)
    const prices = a.map((i) => i.price)
    const lo = Math.min(...prices), hi = Math.max(...prices)
    const last = a.slice().sort((x, y) => (x.d < y.d ? 1 : -1))[0]
    return {
      c, bg: color[c], n: a.length, bags: sum(a, (i) => i.bags), kg: k, val: v, avg: v / k,
      range: lo === hi ? '₹' + num(lo, 1) : '₹' + num(lo, 1) + ' – ' + num(hi, 1),
      last: '₹' + num(last.price, 1), lastDate: dayName(last.d),
      share: v / (val || 1) * 100,
    }
  }).filter((r): r is NonNullable<typeof r> => r !== null).sort((a, b) => b.val - a.val)

  // ---- customer table, waste only: the yarn register names no buyer -----
  const custRows = product !== 'waste' ? [] : (() => {
    const by: Record<string, Inv[]> = {}
    inv.forEach((i) => { const c = i.customer || '(no customer)'; (by[c] = by[c] || []).push(i) })
    return Object.entries(by).map(([c, a]) => {
      const k = sum(a, (i) => i.kg), v = sum(a, (i) => i.exmill)
      const last = a.slice().sort((x, y) => (x.d < y.d ? 1 : -1))[0]
      return { c, n: a.length, kg: k, val: v, avg: k ? v / k : 0, lastDate: dayName(last.d), share: v / (val || 1) * 100 }
    }).sort((a, b) => b.val - a.val)
  })()

  // ---- latest invoices --------------------------------------------------
  const invRows = inv.slice()
    .sort((a, b) => (a.d < b.d ? 1 : a.d > b.d ? -1 : a.no < b.no ? 1 : -1))
    .slice(0, INVOICE_ROWS)

  const scopeName = per === 'all'
    ? monthName(months[0], true) + ' – ' + monthName(months[months.length - 1]) : monthName(per)
  const fy = (() => {
    const y = +months[0].slice(0, 4), m = +months[0].slice(5, 7)
    const start = m >= 4 ? y : y - 1
    return start + '-' + String(start + 1).slice(2)
  })()
  const label: CSSProperties = {
    flex: 'none', whiteSpace: 'nowrap', fontSize: 10, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: 'var(--color-neutral-600)',
  }
  const tickLabel: CSSProperties = { position: 'absolute', right: 0, transform: 'translateY(50%)' }
  const gridLine: CSSProperties = { position: 'absolute', left: 0, right: 0, borderTop: '1px solid var(--color-neutral-200)' }
  const tip: CSSProperties = {
    position: 'absolute', top: 8, pointerEvents: 'none', padding: '10px 12px',
    background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-divider)',
    boxShadow: 'var(--shadow-md)', fontSize: 12, zIndex: 2,
  }

  return (
    <>
      <Subline>
        FY {fy} · {scopeName}{cnt === W.all ? ' · all ' + W.Things.toLowerCase() : ' · ' + cnt} · {num(inv.length)} invoices
      </Subline>

      <div className="no-print" style={{
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: '14px 32px',
        borderBottom: '1px solid var(--color-divider)',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={label}>Period</span>
          <select value={per} style={{ minWidth: 180 }}
            onChange={(e) => { setPeriod(e.target.value); setBHov(null) }}>
            <option value="all">Financial year to date</option>
            {months.slice().reverse().map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={label}>{W.Thing}</span>
          <select value={cnt} style={{ minWidth: 150 }}
            onChange={(e) => { setCount(e.target.value); setBHov(null); setPHov(null) }}>
            <option>{W.all}</option>
            {countsAll.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <UploadLine product={product} onLoaded={reload} source={data?.months.length ? `${all.length} lines on file` : undefined} />
      </div>

      <main style={{ ...MAIN_STYLE, gap: 24 }}>
        <KpiRow items={kpis} />

        {/* Sales by month, or by day within a month */}
        <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
          <PanelHead title={per === 'all' ? 'Sales by month' : 'Sales by day · ' + monthName(per)}
            hint={`Ex-mill value, stacked by ${W.thing} · hover for the split`} />
          <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr)', gap: 8, marginTop: 22 }}>
            <div className="num" style={{ position: 'relative', height: 260, fontSize: 11, color: 'var(--color-neutral-600)' }}>
              {ticks.map((tk) => <span key={tk.pos} style={{ ...tickLabel, bottom: tk.pos }}>{tk.label}</span>)}
            </div>
            <div onMouseLeave={() => setBHov(null)} style={{
              position: 'relative', height: 260,
              borderLeft: '1px solid var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-500)',
            }}>
              {ticks.map((tk) => <span key={tk.pos} style={{ ...gridLine, bottom: tk.pos }} />)}
              <div style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end',
                justifyContent: 'space-around', gap: barGap, padding: '0 10px',
              }}>
                {bars.map((b) => (
                  <div key={b.key} onMouseEnter={() => setBHov(b.key)} style={{
                    position: 'relative', flex: '1 1 0', maxWidth: barMaxW, height: '100%',
                    display: 'flex', flexDirection: 'column-reverse', opacity: b.op,
                  }}>
                    {b.segs.map((s, i) => (
                      <div key={i} style={{ height: s.h.toFixed(2) + '%', background: s.bg, boxShadow: 'inset 0 1px 0 var(--color-bg)' }} />
                    ))}
                    {b.showVal ? (
                      <span className="num" style={{
                        position: 'absolute', bottom: b.top, left: '50%', transform: 'translate(-50%,-4px)',
                        fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                      }}>{num(b.v / 1e7, 2)}</span>
                    ) : null}
                  </div>
                ))}
              </div>
              {barHov ? (
                <div style={{ ...tip, left: barHov.left, transform: barHov.shift, minWidth: 230 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontWeight: 600 }}>
                    <span>{barHov.title}</span><span className="num">{barHov.value}</span>
                  </div>
                  <div style={{ color: 'var(--color-neutral-700)', margin: '2px 0 6px' }}>{barHov.sub}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {barHov.rows.map((r) => (
                      <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 10, height: 10, display: 'block', flex: 'none', background: r.bg }} />
                        <span style={{ flex: 1, color: 'var(--color-neutral-700)' }}>{r.label}</span>
                        <span className="num">{r.kg}</span>
                        <span className="num" style={{ fontWeight: 600, minWidth: 64, textAlign: 'right' }}>{r.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr)', gap: 8 }}>
            <div />
            <div style={{
              display: 'flex', justifyContent: 'space-around', gap: barGap, padding: '6px 10px 0',
              fontSize: per === 'all' ? 12 : 10, color: 'var(--color-neutral-700)',
            }}>
              {bars.map((b) => (
                <span key={b.key} style={{ flex: '1 1 0', maxWidth: barMaxW, textAlign: 'center', whiteSpace: 'nowrap' }}>{b.label}</span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 14 }}>
            {segCounts.map((c) => (
              <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--color-neutral-800)' }}>
                <span style={{ width: 12, height: 12, display: 'block', background: color[c] }} />{c}
              </div>
            ))}
          </div>
        </Blueprint>

        {/* Realisation by count, month over month */}
        <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
          <PanelHead title={`Avg price per kg, month over month by ${W.thing}`}
            hint="Quantity-weighted average ₹/kg ex-mill · hover a month" />
          <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr)', gap: 8, marginTop: 18 }}>
            <div className="num" style={{ position: 'relative', height: 240, fontSize: 11, color: 'var(--color-neutral-600)' }}>
              {pTicks.map((tk) => <span key={tk.pos} style={{ ...tickLabel, bottom: tk.pos }}>{tk.label}</span>)}
            </div>
            <div onMouseLeave={() => setPHov(null)} style={{
              position: 'relative', height: 240,
              borderLeft: '1px solid var(--color-neutral-500)', borderBottom: '1px solid var(--color-neutral-500)',
            }}>
              {pTicks.map((tk) => <span key={tk.pos} style={{ ...gridLine, bottom: tk.pos }} />)}
              <svg viewBox="0 0 1000 240" preserveAspectRatio="none"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}>
                {pLines.map((l) => (
                  <polyline key={l.c} points={l.points} fill="none" stroke={l.bg} strokeWidth={2}
                    strokeDasharray={l.dash} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                ))}
                {priceHov ? (
                  <line x1={priceHov.x} y1={0} x2={priceHov.x} y2={240} strokeWidth={1}
                    vectorEffect="non-scaling-stroke" style={{ stroke: 'var(--color-neutral-700)', opacity: 0.5 }} />
                ) : null}
              </svg>
              {lineCounts.length === 0 ? (
                <div style={{
                  position: 'absolute', inset: 0, display: 'grid', placeItems: 'center',
                  fontSize: 13, color: 'var(--color-neutral-600)', pointerEvents: 'none',
                }}>Select a {W.thing} below to plot its price</div>
              ) : null}
              {pDots.map((p) => (
                <span key={p.c} style={{
                  position: 'absolute', left: p.left, bottom: p.bottom, width: 8, height: 8,
                  transform: 'translate(-50%,50%)', borderRadius: '50%', background: p.bg,
                  border: '2px solid var(--color-bg)', pointerEvents: 'none',
                }} />
              ))}
              <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                {months.map((m) => <div key={m} onMouseEnter={() => setPHov(m)} style={{ flex: '1 1 0', height: '100%' }} />)}
              </div>
              {priceHov ? (
                <div style={{ ...tip, left: priceHov.left, transform: priceHov.shift, minWidth: 200 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{priceHov.title}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {priceHov.rows.map((r) => (
                      <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 12, height: 3, display: 'block', flex: 'none', background: r.bg }} />
                        <span style={{ flex: 1, color: 'var(--color-neutral-700)' }}>{r.label}</span>
                        <span className="num" style={{ fontWeight: 600 }}>₹{num(r.s, 1)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '56px minmax(0,1fr)', gap: 8 }}>
            <div />
            <div style={{ display: 'flex', paddingTop: 6, fontSize: 12, color: 'var(--color-neutral-700)' }}>
              {months.map((m) => (
                <span key={m} style={{ flex: '1 1 0', textAlign: 'center', fontWeight: m === per ? 700 : 400 }}>{monthName(m, true)}</span>
              ))}
            </div>
          </div>
          <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
            <span style={{ ...label, marginRight: 4 }}>{W.Things}</span>
            {pickable.map((c) => (
              <button key={c} type="button" className="legend-toggle" aria-pressed={!pOff[c]}
                onClick={() => { setPOff((o) => ({ ...o, [c]: !o[c] })); setPHov(null) }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                  border: '1px solid ' + (pOff[c] ? 'var(--color-divider)' : 'var(--color-accent-500)'),
                  background: pOff[c] ? 'transparent' : 'var(--color-accent-100)', cursor: 'pointer',
                  font: 'inherit', fontSize: 12, color: 'var(--color-neutral-800)', opacity: pOff[c] ? 0.45 : 1,
                }}>
                <span style={{
                  width: 18, height: 0, display: 'inline-block',
                  borderTop: `3px ${dash[c] === '0' ? 'solid' : dash[c] === '2 3' ? 'dotted' : 'dashed'} ${color[c]}`,
                }} />{c}
              </button>
            ))}
            <button className="btn btn-ghost" type="button" style={{ fontSize: 12, padding: '5px 10px' }}
              onClick={() => { setPOff({}); setPHov(null) }}>All</button>
            <button className="btn btn-ghost" type="button" style={{ fontSize: 12, padding: '5px 10px' }}
              onClick={() => { setPOff(Object.fromEntries(pickable.map((c) => [c, true]))); setPHov(null) }}>None</button>
          </div>
        </Blueprint>

        {/* Count by count (waste: by type) */}
        <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
          <PanelHead title={W.table} hint={scopeName + ' · ex-mill values'} />
          <div className="scroll-x" style={{ marginTop: 14 }}>
            <table className="table" style={{ width: '100%', minWidth: 900 }}>
              <thead>
                <tr>
                  <th>{W.Thing}</th><th style={R}>Invoices</th><th style={R}>Bags</th><th style={R}>Quantity</th>
                  <th style={R}>Ex-mill value</th><th style={R}>Avg ₹/kg</th><th style={R}>Range ₹/kg</th>
                  <th style={R}>Last price</th><th style={{ width: '16%' }}>Share of value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.c}>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, marginRight: 8, background: r.bg }} />{r.c}
                    </td>
                    <td className="num" style={R}>{num(r.n)}</td>
                    <td className="num" style={R}>{num(r.bags)}</td>
                    <td className="num" style={R}>{t(r.kg)}</td>
                    <td className="num" style={{ ...R, fontWeight: 600 }}>{money(r.val)}</td>
                    <td className="num" style={{ ...R, fontWeight: 600 }}>₹{num(r.avg, 1)}</td>
                    <td className="num" style={{ ...R, color: 'var(--color-neutral-700)' }}>{r.range}</td>
                    <td className="num" style={R}>
                      {r.last}<div style={{ fontSize: 11, color: 'var(--color-neutral-600)' }}>{r.lastDate}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ position: 'relative', flex: 1, height: 8, background: 'var(--color-neutral-200)' }}>
                          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: r.share.toFixed(1) + '%', background: r.bg }} />
                        </div>
                        <span className="num" style={{ fontSize: 12, minWidth: 40, textAlign: 'right' }}>{num(r.share, 1)}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{ fontWeight: 600 }}>{W.all}</td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>{num(inv.length)}</td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>{num(sum(inv, (i) => i.bags))}</td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>{t(kgT)}</td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>{money(val)}</td>
                  <td className="num" style={{ ...R, fontWeight: 600 }}>₹{num(avg, 1)}</td>
                  <td /><td /><td />
                </tr>
              </tbody>
            </table>
          </div>
        </Blueprint>

        {/* By customer — the waste register names its buyer; the yarn one does not */}
        {product === 'waste' ? (
          <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
            <PanelHead title="By customer" hint={scopeName + ' · ex-mill values'} />
            <div className="scroll-x" style={{ marginTop: 14 }}>
              <table className="table" style={{ width: '100%', minWidth: 900 }}>
                <thead>
                  <tr>
                    <th>Customer</th><th style={R}>Invoices</th><th style={R}>Quantity</th>
                    <th style={R}>Ex-mill value</th><th style={R}>Avg ₹/kg</th><th style={R}>Last invoice</th>
                    <th style={{ width: '16%' }}>Share of value</th>
                  </tr>
                </thead>
                <tbody>
                  {custRows.map((r) => (
                    <tr key={r.c}>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{r.c}</td>
                      <td className="num" style={R}>{num(r.n)}</td>
                      <td className="num" style={R}>{t(r.kg)}</td>
                      <td className="num" style={{ ...R, fontWeight: 600 }}>{money(r.val)}</td>
                      <td className="num" style={{ ...R, fontWeight: 600 }}>₹{num(r.avg, 1)}</td>
                      <td className="num" style={R}>{r.lastDate}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ position: 'relative', flex: 1, height: 8, background: 'var(--color-neutral-200)' }}>
                            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: r.share.toFixed(1) + '%', background: 'var(--color-accent-700)' }} />
                          </div>
                          <span className="num" style={{ fontSize: 12, minWidth: 40, textAlign: 'right' }}>{num(r.share, 1)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ fontWeight: 600 }}>All customers</td>
                    <td className="num" style={{ ...R, fontWeight: 600 }}>{num(inv.length)}</td>
                    <td className="num" style={{ ...R, fontWeight: 600 }}>{t(kgT)}</td>
                    <td className="num" style={{ ...R, fontWeight: 600 }}>{money(val)}</td>
                    <td className="num" style={{ ...R, fontWeight: 600 }}>₹{num(avg, 1)}</td>
                    <td /><td />
                  </tr>
                </tbody>
              </table>
            </div>
          </Blueprint>
        ) : null}

        {/* Latest invoices */}
        <Blueprint className="sheet" style={{ padding: '22px 24px' }}>
          <PanelHead title="Latest invoices"
            hint={`Most recent ${invRows.length} of ${num(inv.length)} in this selection`} />
          <div className="scroll-x" style={{ marginTop: 14 }}>
            <table className="table" style={{ width: '100%', minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Invoice</th><th>Date</th>{product === 'waste' ? <th>Customer</th> : null}
                  <th>{W.Thing}</th><th style={R}>Bags</th><th style={R}>Quantity</th>
                  <th style={R}>₹/kg</th><th style={R}>Ex-mill</th><th style={R}>GST</th>
                  <th style={R}>Invoice value</th><th style={R}>{W.last}</th>
                </tr>
              </thead>
              <tbody>
                {invRows.map((i, ix) => (
                  <tr key={i.no + ix}>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>{i.no}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{dayName(i.d)}</td>
                    {product === 'waste' ? <td style={{ whiteSpace: 'nowrap' }}>{i.customer}</td> : null}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, marginRight: 7, background: color[i.count] }} />{i.count}
                    </td>
                    <td className="num" style={R}>{num(i.bags)}</td>
                    <td className="num" style={R}>{num(i.kg, 0)} kg</td>
                    <td className="num" style={{ ...R, fontWeight: 600 }}>₹{num(i.price, 1)}</td>
                    <td className="num" style={R}>{lakh(i.exmill)}</td>
                    <td className="num" style={{ ...R, color: 'var(--color-neutral-700)' }}>{lakh(i.gst)}</td>
                    <td className="num" style={R}>{lakh(i.total)}</td>
                    <td className="num" style={R}>{lakh(i.pay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Blueprint>

        <footer className="pretty" style={{
          fontSize: 12, color: 'var(--color-neutral-600)',
          borderTop: '1px solid var(--color-divider)', paddingTop: 14, maxWidth: 900,
        }}>
          {product === 'waste' ? (
            <>
              Source: the waste sales register, FY {fy}. Ex-mill = quantity × price; invoice value adds 5% GST;
              TCS at 1% is collected on some invoices, and the register's total — invoice value plus TCS — is
              what the customer pays. An invoice with several types is several lines here, so "invoices"
              counts lines.
            </>
          ) : (
            <>
              Source: the yarn sales register, FY {fy}. Ex-mill = quantity × price; invoice value adds 5% GST;
              payable is after 0.10% TDS. An invoice with several counts is several lines here, so
              "invoices" counts lines. The register's month summary gives September's quantity as 11,582 — that
              is the bag count; the invoices add up to 5,25,360 kg.
            </>
          )}
        </footer>
      </main>
    </>
  )
}

/** Load a newer register. The register is restated whole, so the sheet
 *  replaces every invoice it names and leaves the rest. A viewer sees only
 *  what is on file. */
function UploadLine({ product, onLoaded, source }: { product: Product; onLoaded: () => void; source?: string }) {
  const canUpload = useCan('upload')
  // An editor hands files in on the Data loads page instead.
  const loadElsewhere = useCan('load') && !canUpload
  const ref = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ text: string; bad?: boolean } | null>(null)

  const upload = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setBusy(true)
    setMsg(null)
    try {
      const r = await sales.upload(file, product)
      setMsg({ text: `${r.source}: ${num(r.rows)} lines on ${num(r.invoices)} invoices, ${r.first_date} to ${r.last_date}; ${num(r.rows_replaced)} lines replaced.` })
      onLoaded()
    } catch (e: any) {
      setMsg({ text: e?.message ?? String(e), bad: true })
    } finally {
      setBusy(false)
      if (ref.current) ref.current.value = ''
    }
  }

  return (
    <div className="no-print" style={{
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginLeft: 'auto',
      fontSize: 12, color: 'var(--color-neutral-700)',
    }}>
      {source ? <span>{source}</span> : null}
      {canUpload ? (
        <label className="tag tag-outline" style={{ cursor: busy ? 'default' : 'pointer' }}>
          {busy ? 'Reading…' : WORDS[product].upload}
          <input ref={ref} type="file" accept=".xlsx" disabled={busy}
            onChange={(e) => upload(e.target.files)} style={{ display: 'none' }} />
        </label>
      ) : null}
      {loadElsewhere ? <LoadElsewhere /> : null}
      {msg ? <span style={{ color: msg.bad ? 'var(--color-bad)' : 'var(--color-good)' }}>{msg.text}</span> : null}
    </div>
  )
}
